import { z } from 'zod'

import { accountStatsSchema } from './accountStats'
import { addressInputSchema, addressSchema, listQueryZodFields } from './system'
import {
  DateDisplayFormat,
  VenueInventorySortOrder,
  VenuePosType,
  VenueFrontendVersion,
  venueScheduleTimeRegex,
  venueSlugMaxLength,
  venueSlugMinLength,
  VenueSubscriptionPlan,
  VenueSubscriptionStatus,
  VenueTagType,
  StripeConnectType,
  VenueType,
  VENUE_LIST_HEALTH_SCORE_POSITIVE,
  VENUE_LIST_HEALTH_SCORE_ZERO,
} from '../constants/venue'
import {
  VenueBillingLifecycleSource,
  VenueBillingPaymentStatus,
  VenueBillingSignupStatus,
} from '../constants/venueBilling'
import { MembershipLevelValue } from '../constants/membership'
import { venueLanguageCodeSchema } from '../constants/locale'
import { feeSchema } from './pricing'
import { imageSchema } from './image'
import { BookingPaymentType } from '../constants/booking'
import { dbEntitySchema } from './system'
import { tagCreateSchema } from './tag'
import { entityAnalyticsSchema } from './track'

const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}|[A-Fa-f0-9]{8})$/

const colorSchema = z.object({
  red: z.number().nullish(),
  green: z.number().nullish(),
  blue: z.number().nullish(),
})

export const venueBrandingSchema = z.object({
  primaryColor: colorSchema.nullish(),
  secondaryColor: colorSchema.nullish(),
  theme: z.enum(['light', 'dark']).nullish(),
})

export const venueBrandingCreateArgsSchema = z.object({
  primaryColor: z
    .string()
    .refine((value) => {
      if (!value || value === '') return true
      return hexColorRegex.test(value)
    })
    .optional()
    .nullable(),
  secondaryColor: z
    .string()
    .refine((value) => {
      if (!value || value === '') return true
      return hexColorRegex.test(value)
    })
    .optional()
    .nullable(),
  theme: z.enum(['light', 'dark']).nullish(),
})

export const scheduleSchema = z.object({
  day: z.number().nullish(),
  closed: z.boolean().nullish(),
  hours: z
    .array(
      z.object({
        start: z.date().nullish(),
        end: z.date().nullish(),
      })
    )
    .nullish(),
})

export const venueLocationSchema = z.object({
  address: addressSchema.nullish(),
  /**
   * @deprecated use venue.city instead
   */
  city: z.string().nullish(),
  /**
   * @deprecated use venue.cityName instead
   */
  cityName: z.string().nullish(),
  neighborhood: z.string().nullish(),
  geo: z.array(z.number()).nullish(),
  /**
   * @deprecated use timezone instead
   */
  timezone: z.string(),
  timezoneOffset: z.number().nullish(),
  // countryCode: z.string().nullish(),
  // locale: z.string().nullish(),
})

export const venueScheduleDaySchema = scheduleSchema
export const venueScheduleDayCreateArgsSchema = scheduleSchema.extend({
  hours: z
    .array(
      z.object({
        start: z.string().regex(venueScheduleTimeRegex).nullish(),
        end: z.string().regex(venueScheduleTimeRegex).nullish(),
      })
    )
    .nullish(),
})
export const venueScheduleSchema = z.array(scheduleSchema)
export const venueScheduleCreateArgsSchema = z.array(
  venueScheduleDayCreateArgsSchema
)

const detailsSchema = z.object({
  maxAge: z.number().nullish(),
  minAge: z.number().nullish(),
  bathrooms: z.number().nullish(),
  shortDescription: z.string().nullish(),
  description: z.string().nullish(),
  video: z.string().nullish(),
  menus: z.array(imageSchema).nullish(),
  floorPlans: z.array(imageSchema).nullish(),
  social: z
    .object({
      facebook: z.string().nullish(),
      twitter: z.string().nullish(),
      instagram: z.string().nullish(),
      yelp: z.string().nullish(),
      tiktok: z.string().nullish(),
    })
    .nullish(),
  bestNights: z.array(z.number()).nullish(),
  maxCapacity: z.number().nullish(),
  floors: z.number().nullish(),
  bars: z.number().nullish(),
  rooms: z.number().nullish(),
  privateTables: z.number().nullish(),
  privateRooms: z.number().nullish(),
  lounges: z.number().nullish(),
})

export const paymentLinkDescriptionTemplateSetSchema = z.object({
  full: z.string().nullish(),
  deposit: z.string().nullish(),
  auth: z.string().nullish(),
})

export const paymentLinkDescriptionTemplatesSchema = z.object({
  reservation: paymentLinkDescriptionTemplateSetSchema.nullish(),
  admission: paymentLinkDescriptionTemplateSetSchema.nullish(),
  full: z.string().nullish(),
  deposit: z.string().nullish(),
  auth: z.string().nullish(),
})

export const configSchema = z.object({
  defaultPoster: imageSchema.nullish(),
  reservationChitText: z.string().nullish(),
  reservationChitTip: z.string().nullish(),
  reservationNote: z.string().nullish(),
  paymentLinkDescriptionTemplates:
    paymentLinkDescriptionTemplatesSchema.nullish(),
  paymentLinkNameTemplate: z.string().nullish(),
  guestlistNote: z.string().nullish(),
  generateTicketQrCodes: z.boolean().nullish(),
  generateGuestlistQrCodes: z.boolean().nullish(),
  guestlistMax: z.number().nullish(),
  chitShowExitTime: z.boolean().nullish(),
  posAutoClose: z.boolean().nullish(),
  defaultReservationRequestEnabled: z.boolean().nullish(),
  defaultGuestlistEnabledInternally: z.boolean().nullish(),
  defaultGuestlistEnabledExternally: z.boolean().nullish(),
  defaultTallyEnabledInternally: z.boolean().nullish(),
  defaultGuestlistCutoff: z.string().nullish(),
  /** Custom substatus labels for AdmissionGroupType.TABLE, configurable per venue. */
  reservationSubstatuses: z.array(z.string().min(1)).nullish(),
  /** Custom substatus labels for GUEST_LIST/TICKET/TALLY admission types, configurable per venue. */
  admissionSubstatuses: z.array(z.string().min(1)).nullish(),
})

export const venueSubscriptionPrivelagesSchema = z.object({
  canUseReservations: z.boolean().nullish(),
  canUseReports: z.boolean().nullish(),
  canUseCustomerChat: z.boolean().nullish(),
  canUsePosIntegration: z.boolean().nullish(),
})

export const venueSubscriptionAddOnsSchema = z.object({
  customerChat: z.boolean().nullish(),
  posIntegration: z.boolean().nullish(),
})

export const venueBillingLifecycleSchema = z.object({
  signupStatus: z.nativeEnum(VenueBillingSignupStatus),
  paymentStatus: z.nativeEnum(VenueBillingPaymentStatus),
  statusReason: z.string().nullable(),
  updatedAt: z.date(),
  source: z.nativeEnum(VenueBillingLifecycleSource),
})

export const venueFraudSettingsSchema = z.object({
  customerLowTag: z.boolean().nullish(),
  customerMediumTag: z.boolean().nullish(),
  customerHighTag: z.boolean().nullish(),
  admissionGroupLowTag: z.boolean().nullish(),
  admissionGroupMediumTag: z.boolean().nullish(),
  admissionGroupHighTag: z.boolean().nullish(),
})

export const venueProductCategoryPricingSchema = z.object({
  taxRate: z.number().min(0).max(1).nullish(),
  serviceChargeRate: z.number().min(0).max(1).nullish(),
  fees: z.array(feeSchema).nullish(),
  userPaysProcessing: z.boolean().nullish(),
  paymentType: z.nativeEnum(BookingPaymentType).nullish(),
  termsAndConditions: z.string().nullish(),
})

export const venueProductCategorySchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  /**
   * @description Server-derived from `name` (normalized uppercase alphanumeric)
   */
  category: z.string().min(1).nullish(),
  isSystem: z.boolean().nullish(),
  sortOrder: z.number().int().nullish(),
  pricing: venueProductCategoryPricingSchema.nullish(),
})

export const venueStripePaymentMethodsSchema = z.object({
  card: z.boolean().nullish(),
  link: z.boolean().nullish(),
  usBankAccount: z.boolean().nullish(),
  crypto: z.boolean().nullish(),
})

export const venueStripePaymentMethodsUpdateSchema =
  venueStripePaymentMethodsSchema.superRefine((value, ctx) => {
    const hasProvidedValue = Object.values(value).some(
      (isEnabled) => isEnabled != null
    )
    if (!hasProvidedValue) return

    const hasEnabledPaymentMethod = Object.values(value).some(
      (isEnabled) => isEnabled === true
    )
    if (!hasEnabledPaymentMethod) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one Stripe payment method must be enabled',
      })
    }
  })

const venueProductCategoryPricingByIdSchema =
  venueProductCategoryPricingSchema.extend({
    productCategoryId: z.string(),
  })

export const venueSchema = dbEntitySchema.extend({
  name: z.string(),
  timezone: z.string(),
  type: z.nativeEnum(VenueType),
  slug: z.string().min(venueSlugMinLength).max(venueSlugMaxLength),
  buyFrontendVersion: z.nativeEnum(VenueFrontendVersion).nullish(),
  logo: z.string().nullish(),
  organization: z.string(),
  organizationName: z.string(),
  /**
   * City id. Preferred over deprecated `location.city`.
   */
  city: z.string().nullish(),
  cityName: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  enable: z.boolean().nullish(),
  visibility: z.number().nullish(),
  ticketConfirmationEmailSubject: z.string().nullish(),
  reservationConfirmationEmailSubject: z.string().nullish(),
  images: z.array(imageSchema).min(0).max(10).nullish(),
  primaryImage: imageSchema.nullish(),
  // tags: z.array(baseTagSchema).nullish(),
  tags: z
    .array(
      z.object({
        key: z.nativeEnum(VenueTagType),
        name: z.string(),
        value: z.string(),
      })
    )
    .nullish(),
  inventoryTimeframe: z.number().min(1).nullish(),
  inventoryHoldMinutes: z.number().min(0).max(120).nullish(),
  inventorySortOrder: z.nativeEnum(VenueInventorySortOrder).nullish(),
  publish: z
    .object({
      tablelist: z.boolean().nullish(),
      discotech: z.boolean().nullish(),
      cymbal: z.boolean().nullish(),
      swivul: z.boolean().nullish(),
    })
    .nullish(),
  addressFormatted: z.string().nullish(),
  billingAddress: addressSchema.nullish(),
  location: venueLocationSchema.partial(),
  company: z.string().nullish(),
  schedule: z.array(scheduleSchema).nullish(),
  currencyCode: z.string().nullish(),
  dateDisplayFormat: z.nativeEnum(DateDisplayFormat).nullish(),
  currencySymbol: z.string().nullish(), //readonly - server-derived from currencyCode
  languageCode: z.string().nullish(),
  notes: z.string().nullish(),
  reservation: z.boolean().nullish(),
  appendReservationIdToNotes: z.boolean().nullish(),
  accessLevel: z.nativeEnum(MembershipLevelValue).nullish(),
  details: detailsSchema.nullish(),
  website: z.string().nullish(),
  ticketTerms: z.string().nullish(),
  tableTerms: z.string().nullish(),
  ticketPDFAd: z.string().nullish(),
  fees: z.array(feeSchema).nullish(),
  tax: z.number().min(0).max(1).nullish(),
  tip: z.number().min(0).max(1).nullish(),
  ticketFees: z.array(feeSchema).nullish(),
  ticketTax: z.number().min(0).max(1).nullish(),
  tableFees: z.array(feeSchema).nullish(),
  //
  isUnderReview: z.boolean().nullish(),
  isLocked: z.boolean().nullish(),
  isPastDue: z.boolean().nullish(),
  isSellingApproved: z.boolean().nullish(),
  isConfirmationRequestApproved: z.boolean().nullish(),
  tableProcessingFee: z.number().nullish(),
  venueTableProcessingFee: z.number().nullish(),
  ticketProcessingFee: z.number().nullish(),
  venueTicketProcessingFee: z.number().nullish(),
  ticketCommissionDirect: z.number().nullish(),
  ticketCommissionIndirect: z.number().nullish(),
  tableCommissionDirect: z.number().nullish(),
  tableCommissionIndirect: z.number().nullish(),
  stripeConnectEnabled: z.boolean().nullish(),
  stripeConnectType: z.nativeEnum(StripeConnectType).nullish(),
  stripeAccountCountry: z.string().nullish(),
  stripePaymentMethods: venueStripePaymentMethodsSchema.nullish(),
  //subscriptions
  externalCustomerId: z.string().nullish(),
  externalSubscriptionId: z.string().nullish(),
  externalSubscriptionNumber: z.string().nullish(),
  externalSubscriptionInterval: z.number().nullish(),
  externalSubscriptionIntervalUnit: z.string().nullish(),
  externalSubscriptionSalesPersonName: z.string().nullish(),
  externalSubscriptionExpiresAt: z.date().nullish(),
  externalSubscriptionPlanName: z.string().nullish(),
  externalSubscriptionPlanCode: z.string().nullish(),
  externalSubscriptionPlanPrice: z.number().nullish(),
  externalSubscriptionStatus: z.string().nullish(),
  subscriptionStatus: z.nativeEnum(VenueSubscriptionStatus).nullish(),
  subscriptionActive: z.boolean().nullish(),
  isPayingNonSubscribed: z.boolean().nullish(),
  payingParentVenueId: z.string().nullish(),
  billingLifecycle: venueBillingLifecycleSchema.nullish(),
  stats: accountStatsSchema.nullish(),
  analytics: entityAnalyticsSchema.nullish(),
  subscriptionPlan: z.nativeEnum(VenueSubscriptionPlan).nullish(),
  signUpInfo: z
    .object({
      posName: z.string().nullish(),
    })
    .nullish(),
  subscriptionAddOns: venueSubscriptionAddOnsSchema.nullish(),
  subscriptionPrivelages: venueSubscriptionPrivelagesSchema.nullish(),
  // ---------------------- //
  /**
   * @deprecated Legacy Stripe account id.
   */
  paymentProviderAccountId: z.string().nullish(),
  statementDescriptor: z.string().max(255).nullish(),
  //customer chat
  customerChatPhoneNumber: z.string().nullish(),
  customerChatEmailAddress: z.string().email().nullish(),
  trackingFacebookPixelId: z.string().nullish(),
  eventDefault: z
    .object({
      isMapWidgetEnabled: z.boolean().nullish(),
    })
    .nullish(),
  config: configSchema.nullish(),
  meta: z
    .object({
      description: z.string().nullish(),
      image: z.string().nullish(),
    })
    .nullish(),
  showMarketingOptIn: z.boolean().nullish(),
  marketingOptInText: z.string().nullish(),
  marketingOptInHeader: z.string().nullish(),
  showLoyaltyRewards: z.boolean().nullish(),
  fraudSettings: venueFraudSettingsSchema.nullish(),
  //
  branding: venueBrandingSchema.nullish(),
  inventoryTableSubtotalPreviews: z.boolean().nullish(),
  enablePosCodes: z.boolean().nullish(),
  /**
   * When true, external PUBLIC receipts override TPRO INTERNAL booking receipts
   * for TABLE spent. When false, both contribute.
   */
  preferExternalReceiptsForSpend: z.boolean().nullish(),
  // integration data
  posType: z.nativeEnum(VenuePosType).nullish(),
  /**
   * @description this is specific to the Omnivore integration - poorly named
   */
  posLocationId: z.string().nullish(),
})

export const venueCreateArgsSchema = venueSchema
  .omit({
    _id: true,
    id: true,
    created: true,
    modified: true,
    deleted: true,
    organization: true,
    organizationName: true,
    schedule: true,
    slug: true,
    location: true,
    type: true,
    analytics: true,
    isConfirmationRequestApproved: true,
    subscriptionPlan: true,
    tags: true,
    branding: true,
    buyFrontendVersion: true,
  })
  .extend({
    type: z.nativeEnum(VenueType).nullish(),
    subscriptionPlan: z.nativeEnum(VenueSubscriptionPlan).nullish(),
    schedule: z.array(venueScheduleDayCreateArgsSchema).nullish(),
    cityId: z.string().nullish(),
    //address fields
    address: addressInputSchema.nullish(),
    city: z.string().nullish(),
    neighborhood: z.string().nullish(),
    organizationId: z.string(),
    facebook: z.string().nullish(),
    twitter: z.string().nullish(),
    instagram: z.string().nullish(),
    yelp: z.string().nullish(),
    tiktok: z.string().nullish(),
    //details
    maxAge: z.number().nullish(),
    minAge: z.number().nullish(),
    bathrooms: z.number().nullish(),
    shortDescription: z.string().nullish(),
    description: z.string().nullish(),
    menus: z.array(imageSchema).nullish(),
    floorPlans: z.array(imageSchema).nullish(),
    social: z
      .object({
        facebook: z.string().url().nullish(),
        twitter: z.string().url().nullish(),
        instagram: z.string().url().nullish(),
        yelp: z.string().url().nullish(),
        tiktok: z.string().url().nullish(),
      })
      .optional()
      .nullable(),
    video: z.string().url().nullish(),
    bestNights: z.array(z.number()).nullish(),
    maxCapacity: z.number().nullish(),
    floors: z.number().nullish(),
    bars: z.number().nullish(),
    rooms: z.number().nullish(),
    privateTables: z.number().nullish(),
    privateRooms: z.number().nullish(),
    lounges: z.number().nullish(),
    tags: z.array(tagCreateSchema).nullish(),
    //add ons
    enableCustomerChat: z.boolean().nullish(),
    enablePosIntegration: z.boolean().nullish(),
    branding: venueBrandingCreateArgsSchema.nullish(),
    languageCode: venueLanguageCodeSchema.nullish(),
    stripePaymentMethods: venueStripePaymentMethodsUpdateSchema.nullish(),
  })

export const venueUpdateByIdArgsSchema = venueSchema
  .partial()
  .omit({
    organization: true,
    organizationName: true,
    id: true,
    created: true,
    modified: true,
    deleted: true,
    // details: true,
    name: true,
    type: true,
    schedule: true,
    tags: true,
    analytics: true,
    config: true,
    isConfirmationRequestApproved: true,
    subscriptionAddOns: true,
    branding: true,
  })
  .extend({
    name: z.string().nullish(),
    schedule: z.array(venueScheduleDayCreateArgsSchema).nullish(),
    address: addressInputSchema.nullish(),
    type: z.nativeEnum(VenueType).nullish(),
    cityId: z.string().nullish(),
    /**
     * @deprecated use cityId instead
     */
    city: z.string().nullish(),
    'venga.id': z.string().nullish(),
    'venga.username': z.string().nullish(),
    'venga.password': z.string().nullish(),
    facebook: z.string().nullish(),
    twitter: z.string().nullish(),
    instagram: z.string().nullish(),
    yelp: z.string().nullish(),
    tiktok: z.string().nullish(),
    //event settings
    isMapWidgetEnabled: z.boolean().nullish(),
    //config
    reservationNote: z.string().nullish(),
    guestlistNote: z.string().nullish(),
    reservationChitText: z.string().nullish(),
    reservationChitTip: z.string().nullish(),
    generateTicketQrCodes: z.boolean().nullish(),
    generateGuestlistQrCodes: z.boolean().nullish(),
    guestlistMax: z.number().nullish(),
    posAutoClose: z.boolean().nullish(),
    reservationSubstatuses: z.array(z.string().min(1)).nullish(),
    admissionSubstatuses: z.array(z.string().min(1)).nullish(),
    paymentLinkDescriptionFull: z.string().nullish(),
    paymentLinkDescriptionDeposit: z.string().nullish(),
    paymentLinkDescriptionAuth: z.string().nullish(),
    paymentLinkDescriptionReservationFull: z.string().nullish(),
    paymentLinkDescriptionReservationDeposit: z.string().nullish(),
    paymentLinkDescriptionReservationAuth: z.string().nullish(),
    paymentLinkDescriptionAdmissionFull: z.string().nullish(),
    paymentLinkNameTemplate: z.string().nullish(),
    //details
    maxAge: z.number().nullish(),
    minAge: z.number().nullish(),
    bathrooms: z.number().nullish(),
    shortDescription: z.string().nullish(),
    description: z.string().nullish(),
    video: z.string().nullish(),
    menus: z.array(imageSchema).nullish(),
    defaultPoster: imageSchema.nullish(),
    floorPlans: z.array(imageSchema).nullish(),
    social: z
      .object({
        facebook: z.string().nullish(),
        twitter: z.string().nullish(),
        instagram: z.string().nullish(),
        yelp: z.string().nullish(),
        tiktok: z.string().nullish(),
      })
      .optional()
      .nullable(),
    bestNights: z.array(z.number()).nullish(),
    maxCapacity: z.number().nullish(),
    floors: z.number().nullish(),
    bars: z.number().nullish(),
    rooms: z.number().nullish(),
    privateTables: z.number().nullish(),
    privateRooms: z.number().nullish(),
    lounges: z.number().nullish(),
    neighborhood: z.string().nullish(),
    tags: z.array(tagCreateSchema).nullish(),
    subscriptionPlan: z.nativeEnum(VenueSubscriptionPlan).nullish(),
    venga: z
      .object({
        id: z.string().nullish(),
        username: z.string().nullish(),
        password: z.string().nullish(),
      })
      .nullish(),
    //add ons
    enableCustomerChat: z.boolean().nullish(),
    enablePosIntegration: z.boolean().nullish(),
    branding: venueBrandingCreateArgsSchema.nullish(),
    languageCode: venueLanguageCodeSchema.nullish(),
    stripePaymentMethods: venueStripePaymentMethodsUpdateSchema.nullish(),
  })

const queryBoolean = z
  .string()
  .transform((value) => {
    if (value === 'true') {
      return true
    } else if (value === 'false') {
      return false
    } else {
      throw new Error("The string must be 'true' or 'false'")
    }
  })
  .nullish()

export const venuesListQuerySchema = z.object({
  ...listQueryZodFields,
  id: z.string().or(z.array(z.string())).nullish(),
  enable: queryBoolean,
  publishTablelist: queryBoolean,
  locked: queryBoolean,
  search: z.string().nullish(),
  stripeConnectEnabled: queryBoolean,
  underReview: queryBoolean,
  pastDue: queryBoolean,
  subscriptionStatus: z
    .nativeEnum(VenueSubscriptionStatus)
    .or(z.array(z.nativeEnum(VenueSubscriptionStatus)))
    .nullish(),
  isPayingNonSubscribed: queryBoolean,
  description: z.string().nullish(),
  shortDescription: z.string().nullish(),
  organizationId: z.string().nullish(),
  cityId: z.string().nullish(),
  healthScore: z
    .enum([VENUE_LIST_HEALTH_SCORE_ZERO, VENUE_LIST_HEALTH_SCORE_POSITIVE])
    .nullish(),
  visibility: z.coerce.number().nullish(),
  visibilityMin: z.coerce.number().nullish(),
  accessLevel: z.coerce.number().nullish(),
  accessLevelMax: z.coerce.number().nullish(),
  consumerPaying: queryBoolean,
  externalSubscriptionStatus: z.preprocess((val) => {
    if (val === undefined || val === null || val === '') return undefined
    if (Array.isArray(val)) {
      const arr = val
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter(Boolean)
      return arr.length ? arr : undefined
    }
    const s = String(val).trim()
    return s || undefined
  }, z.union([z.string(), z.array(z.string())]).optional()),
  subscriptionActive: queryBoolean,
})

export const venuesListResponseSchema = z.object({
  data: z.array(venueSchema),
})
